import {
  AdditiveBlending,
  BackSide,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  DirectionalLight,
  DoubleSide,
  Fog,
  Group,
  HemisphereLight,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  Points,
  PointsMaterial,
  Quaternion,
  RepeatWrapping,
  type Scene,
  ShaderMaterial,
  SphereGeometry,
  SRGBColorSpace,
  Vector3,
} from 'three';
import { chunkObstacles, OBSTACLE_RADII } from '@gra/engine';
import { Box3 } from 'three';
import type { Assets } from './assets';
import { glow } from './fx';
import { Particles } from './particles';
import { WORLD_SCALE, type PropDef, type ZoneLook } from './visuals';
import type { LightSpot } from './lights';

const CHUNK = 16;
/** Drobnica (kości, dynie, świece) na kawałek mapy — da się przez nią przejść. */
const DECOR_PER_CHUNK = 6;
/** Wokół punktu startu nie stawiamy dekoracji, żeby nie zasłaniały bohatera na początku. */
const CLEAR_RADIUS = 5;
/**
 * Co tyle jednostek powtarza się tekstura terenu. 12, nie 7: kreski trawy wychodzą szersze niż piksel ekranu
 * (przy 7 były cieńsze i przy ruchu mrowiły jak zepsute piksele).
 */
const GROUND_TILE = 12;
const MOTES = 140;
/** Osie kamery cienia słońca (słońce stoi w (-14, 26, 10) od celu) — do przesuwania mapy cienia o całe piksele. */
const SUN_DIR = new Vector3(14, -26, -10).normalize();
const SUN_RIGHT = new Vector3(0, 1, 0).cross(SUN_DIR.clone().negate()).normalize();
const SUN_UP = SUN_DIR.clone().negate().cross(SUN_RIGHT).normalize();

// ---------- malowane tekstury terenu ----------

function seeded(seed: number): () => number {
  return () => (seed = (seed * 16807) % 2147483647) / 2147483647;
}

/** Rysuje kształt z zawinięciem na krawędziach, żeby tekstura była bezszwowa. */
function wrapped(size: number, draw: (dx: number, dy: number) => void): void {
  for (const dx of [-size, 0, size]) for (const dy of [-size, 0, size]) draw(dx, dy);
}

function shade(hex: string, k: number, jitter = 0, rnd = Math.random): string {
  const c = new Color(hex);
  const j = (rnd() - 0.5) * jitter;
  c.setRGB(Math.min(1, c.r * (k + j)), Math.min(1, c.g * (k + j * 1.2)), Math.min(1, c.b * (k + j * 0.8)));
  return `#${c.getHexString()}`;
}

/** Ręcznie „malowana” tekstura: plamy tonalne + krótkie pociągnięcia pędzla. */
function paint(kind: 'grass' | 'dirt' | 'stone', base: string, dark: string, seed: number): CanvasTexture {
  const size = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const rnd = seeded(seed);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);

  // Bez dużych plam jasności: tekstura powtarza się co kilka metrów, więc przy biegu ta sama ciemna plama
  // co chwilę wracała w to samo miejsce ekranu i wyglądała, jakby szła za postacią.

  if (kind === 'grass') {
    // Pociągnięcia pędzla — źdźbła w różnych odcieniach, z lekkim pochyleniem.
    ctx.lineCap = 'round';
    for (let i = 0; i < 16000; i++) {
      const x = rnd() * size;
      const y = rnd() * size;
      const len = 5 + rnd() * 11;
      const ang = -Math.PI / 2 + (rnd() - 0.5) * 0.9;
      // Mniejszy kontrast kresek: drobne jasne/ciemne punkty mniej migoczą przy ruchu kamery.
      ctx.strokeStyle = shade(rnd() < 0.3 ? dark : base, 0.85 + rnd() * 0.4, 0.25, rnd);
      ctx.globalAlpha = 0.4 + rnd() * 0.4;
      ctx.lineWidth = 1.2 + rnd() * 1.8;
      wrapped(size, (dx, dy) => {
        ctx.beginPath();
        ctx.moveTo(x + dx, y + dy);
        ctx.quadraticCurveTo(x + dx + Math.cos(ang) * len * 0.5 + 2, y + dy + Math.sin(ang) * len * 0.5, x + dx + Math.cos(ang) * len, y + dy + Math.sin(ang) * len);
        ctx.stroke();
      });
    }
    // Drobne kwiatki.
    for (let i = 0; i < 40; i++) {
      const x = rnd() * size;
      const y = rnd() * size;
      ctx.globalAlpha = 0.9;
      ctx.fillStyle = ['#f3e27a', '#f2f0e6', '#c9a0e8', '#f5a96b'][Math.floor(rnd() * 4)]!;
      wrapped(size, (dx, dy) => {
        ctx.beginPath();
        ctx.arc(x + dx, y + dy, 1.6 + rnd() * 1.4, 0, Math.PI * 2);
        ctx.fill();
      });
    }
  } else if (kind === 'dirt') {
    for (let i = 0; i < 2800; i++) {
      const x = rnd() * size;
      const y = rnd() * size;
      const r = 1 + rnd() * 3.5;
      ctx.globalAlpha = 0.35 + rnd() * 0.5;
      ctx.fillStyle = shade(base, 0.6 + rnd() * 0.9, 0.2, rnd);
      wrapped(size, (dx, dy) => {
        ctx.beginPath();
        ctx.ellipse(x + dx, y + dy, r * 1.4, r, rnd() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      });
    }
  } else {
    // Bruk: nieregularne kamienie z jasną górną i ciemną dolną krawędzią.
    for (let i = 0; i < 260; i++) {
      const x = rnd() * size;
      const y = rnd() * size;
      const r = 18 + rnd() * 22;
      const pts = Array.from({ length: 7 }, (_, k) => {
        const a = (k / 7) * Math.PI * 2 + rnd() * 0.4;
        const rr = r * (0.75 + rnd() * 0.3);
        return [Math.cos(a) * rr, Math.sin(a) * rr * 0.8] as const;
      });
      const fill = shade(base, 0.8 + rnd() * 0.45, 0.15, rnd);
      wrapped(size, (dx, dy) => {
        ctx.globalAlpha = 1;
        ctx.beginPath();
        pts.forEach(([px, py], k) => (k ? ctx.lineTo(x + dx + px, y + dy + py) : ctx.moveTo(x + dx + px, y + dy + py)));
        ctx.closePath();
        ctx.fillStyle = fill;
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = shade(dark, 0.7);
        ctx.stroke();
        const g = ctx.createLinearGradient(x + dx, y + dy - r, x + dx, y + dy + r);
        g.addColorStop(0, 'rgba(255,255,255,0.25)');
        g.addColorStop(1, 'rgba(0,0,0,0.25)');
        ctx.fillStyle = g;
        ctx.fill();
      });
    }
  }
  ctx.globalAlpha = 1;
  const tex = new CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 16;
  return tex;
}

const NOISE_GLSL = /* glsl */ `
  float hash21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash21(i), hash21(i + vec2(1, 0)), u.x), mix(hash21(i + vec2(0, 1)), hash21(i + vec2(1, 1)), u.x), u.y);
  }
  float fbm(vec2 p) { float v = 0.0, a = 0.5; for (int i = 0; i < 4; i++) { v += a * vnoise(p); p *= 2.03; a *= 0.5; } return v; }
`;

/**
 * Teren: trzy malowane tekstury (trawa, ziemia, bruk) mieszane szumem w przestrzeni świata.
 * Bruk układa się w kręte ścieżki. Oświetlenie, cienie i mgła działają normalnie (MeshStandardMaterial).
 */
function groundMaterial(look: ZoneLook): MeshStandardMaterial {
  const g = look.ground;
  const uniforms = {
    uGrass: { value: paint('grass', g.grass, g.grassDark, 11) },
    uDirt: { value: paint('dirt', g.dirt, shade(g.dirt, 0.6), 23) },
    uStone: { value: paint('stone', g.stone, shade(g.stone, 0.5), 37) },
    uTile: { value: GROUND_TILE },
    uDirtAmt: { value: g.dirtAmount },
    uStoneAmt: { value: g.stoneAmount },
  };
  const mat = new MeshStandardMaterial({ roughness: 1, color: '#ffffff' });
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWorld;')
      // Faktura pędzla (painted.ts) liczy się w układzie modelu, a ziemia jedzie za bohaterem — bez tego
      // jej plamy przesuwały się razem z postacią. Dla ziemi faktura bierze pozycję w świecie.
      .replace('#include <project_vertex>', '#include <project_vertex>\nvWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvPaintPos = vWorld;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        varying vec3 vWorld;
        uniform sampler2D uGrass, uDirt, uStone;
        uniform float uTile, uDirtAmt, uStoneAmt;
        ${NOISE_GLSL}`,
      )
      .replace(
        '#include <map_fragment>',
        `vec2 wuv = vWorld.xz / uTile;
        vec3 grass = texture2D(uGrass, wuv).rgb;
        vec3 dirt = texture2D(uDirt, wuv * 0.8 + 0.37).rgb;
        vec3 stone = texture2D(uStone, wuv * 0.6).rgb;
        float n = fbm(vWorld.xz * 0.05);
        float wDirt = smoothstep(0.62 - uDirtAmt * 0.3, 0.78 - uDirtAmt * 0.3, n + fbm(vWorld.xz * 0.3) * 0.15);
        float path = 1.0 - abs(fbm(vWorld.xz * 0.025 + 31.0) * 2.0 - 1.0);
        float wStone = smoothstep(0.9, 0.95, path) * uStoneAmt;
        vec3 col = mix(grass, dirt, wDirt);
        col = mix(col, stone, wStone);
        // Bez dużych plam światła i cienia (fbm): gracz widział je jako ciemne pola, które chodzą za postacią.
        diffuseColor.rgb *= col;`,
      );
  };
  mat.customProgramCacheKey = () => 'painted-ground';
  return mat;
}

function skyMaterial(look: ZoneLook): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      uZenith: { value: new Color(look.sky.zenith) },
      uHorizon: { value: new Color(look.sky.horizon) },
      uClouds: { value: look.sky.clouds },
      uTime: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vDir;
      void main() { vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform vec3 uZenith, uHorizon; uniform float uClouds, uTime;
      varying vec3 vDir;
      ${NOISE_GLSL}
      void main() {
        float h = max(vDir.y, 0.0);
        vec3 col = mix(uHorizon, uZenith, pow(h, 0.55));
        vec2 p = vDir.xz / (vDir.y + 0.25) * 2.0 + vec2(uTime * 0.01, 0.0);
        float c = smoothstep(0.5, 0.8, fbm(p)) * uClouds * smoothstep(0.0, 0.25, h);
        col = mix(col, mix(uHorizon, vec3(1.0), 0.6), c);
        gl_FragColor = vec4(col, 1.0);
      }`,
    side: BackSide,
    depthWrite: false,
    fog: false,
  });
}

/** Zaokrąglony, lekko nieregularny kamień (gładkie cieniowanie zamiast ostrych ścianek). */
function roundedRock(): BufferGeometry {
  const g = new IcosahedronGeometry(0.35, 2);
  const pos = g.attributes.position as BufferAttribute;
  const v = new Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const n = 1 + 0.18 * Math.sin(v.x * 9.1 + v.z * 4.3) * Math.cos(v.y * 7.7) + 0.08 * Math.sin(v.z * 17.0);
    v.multiplyScalar(n);
    if (v.y < 0) v.y *= 0.4; // spłaszczony spód — kamień leży, a nie lewituje
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  g.computeVertexNormals();
  return g;
}

/**
 * Kępka źdźbeł: trzy zakrzywione, zwężające się paski skrzyżowane pod kątem.
 * Normalne skierowane w górę — trawa oświetla się miękko jak powierzchnia, bez ostrych krawędzi.
 */
function grassBlades(): BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const index: number[] = [];
  const SEG = 4;
  for (let b = 0; b < 3; b++) {
    const a = (b / 3) * Math.PI;
    const dx = Math.cos(a);
    const dz = Math.sin(a);
    const lean = 0.12 + b * 0.05;
    const base = positions.length / 3;
    for (let i = 0; i <= SEG; i++) {
      const t = i / SEG;
      const w = 0.07 * (1 - t * 0.92);
      const y = t * 0.62;
      const bend = lean * t * t;
      for (const side of [-1, 1]) {
        positions.push(dx * w * side - dz * bend, y, dz * w * side + dx * bend);
        normals.push(0, 1, 0);
      }
      if (i < SEG) {
        const k = base + i * 2;
        index.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
      }
    }
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  g.setAttribute('normal', new BufferAttribute(new Float32Array(normals), 3));
  g.setIndex(index);
  return g;
}

function hash(x: number, y: number, i: number): number {
  let h = (x * 374761393 + y * 668265263 + i * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * Otoczenie strefy: malowany teren, niebo, światło (słońce + niebo + kontra), mgła,
 * trawa na wietrze, drobiny w powietrzu i dekoracje generowane w kawałkach wokół kamery.
 */
export class Environment {
  readonly sun: DirectionalLight;
  private readonly back: DirectionalLight;
  private readonly ground: Mesh;
  private readonly sky: Mesh;
  private readonly chunks = new Map<string, Group>();
  private readonly decor = new Group();
  private readonly rockGeo = roundedRock();
  private readonly rockMat: MeshStandardMaterial;
  private readonly grassGeo = grassBlades();
  private readonly grassMat: MeshStandardMaterial;
  private readonly wind = { value: 0 };
  private readonly motes: Points;
  private readonly moteSeeds: Float32Array;
  private readonly props: { def: PropDef; assets: Assets }[] = [];
  private propWeight = 0;
  /** Przeszkody z silnika: seed wyprawy i gęstość strefy (brak = obóz, bez przeszkód). */
  private readonly obstacles: { seed: number; count: number } | null;
  private readonly footprint = new Map<string, number>();
  private readonly center = new Vector3();
  private readonly ambience: Particles | null = null;
  private readonly ambienceKind: ZoneLook['ambience'];
  private ambienceCarry = 0;
  /** Kolor kurzu spod stóp — pasuje do ziemi strefy. */
  readonly dust: string;
  private time = 0;

  constructor(scene: Scene, look: ZoneLook, options: { decor: boolean; assets?: Assets; obstacles?: { seed: number; count: number } }) {
    this.obstacles = options.obstacles ?? null;
    this.dust = look.ground.dirt;
    this.ambienceKind = look.ambience;
    if (look.ambience) this.ambience = new Particles(scene, 700, 0.2);
    if (options.assets) {
      for (const def of look.props) {
        this.props.push({ def, assets: options.assets });
        this.propWeight += def.weight;
      }
    }
    const horizon = new Color(look.sky.horizon);
    scene.background = horizon;
    scene.fog = new Fog(horizon, look.fog[0], look.fog[1]);

    scene.add(new HemisphereLight(look.fill.sky, look.fill.ground, look.fill.intensity));
    this.sun = new DirectionalLight(look.sun.color, look.sun.intensity);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(4096, 4096);
    const sc = this.sun.shadow.camera;
    sc.left = sc.bottom = -24;
    sc.right = sc.top = 24;
    sc.near = 1;
    sc.far = 90;
    this.sun.shadow.bias = -0.0005;
    // 0,05, nie 0,02: przy mniejszym postacie zacieniały same siebie w poziome paski (trądzik cienia).
    this.sun.shadow.normalBias = 0.05;
    this.sun.shadow.radius = 1.5; // lekko zmiękczone, ale wyraźne krawędzie cieni
    this.back = new DirectionalLight(look.back.color, look.back.intensity);
    scene.add(this.sun, this.sun.target, this.back, this.back.target);

    this.ground = new Mesh(new PlaneGeometry(200, 200), groundMaterial(look));
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.receiveShadow = true;
    scene.add(this.ground);

    this.sky = new Mesh(new SphereGeometry(95, 32, 16), skyMaterial(look));
    this.sky.renderOrder = -1;
    scene.add(this.sky);

    this.rockMat = new MeshStandardMaterial({ color: look.rocks, roughness: 0.92 });
    this.grassMat = new MeshStandardMaterial({ color: look.blades, roughness: 1, side: DoubleSide });
    this.grassMat.onBeforeCompile = (shader) => {
      shader.uniforms.uWind = this.wind;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uWind;\nvarying float vH;')
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
          vH = clamp(position.y / 0.62, 0.0, 1.0);
          vec4 root = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          float sway = sin(uWind * 1.7 + root.x * 0.35 + root.z * 0.27) + 0.4 * sin(uWind * 3.1 + root.x);
          transformed.x += sway * 0.09 * vH * vH;
          transformed.z += sway * 0.05 * vH * vH;`,
        );
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying float vH;')
        .replace('#include <color_fragment>', '#include <color_fragment>\ndiffuseColor.rgb *= mix(0.45, 1.35, vH);');
    };
    this.grassMat.customProgramCacheKey = () => 'wind-grass';

    // Drobiny w powietrzu: świetliki o zmierzchu, pyłki w dzień.
    const positions = new Float32Array(MOTES * 3);
    this.moteSeeds = new Float32Array(MOTES * 4);
    for (let i = 0; i < MOTES; i++) {
      this.moteSeeds.set([Math.random() * 30 - 15, 0.4 + Math.random() * 3.5, Math.random() * 30 - 15, Math.random() * 100], i * 4);
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(positions, 3));
    this.motes = new Points(
      g,
      new PointsMaterial({ map: glow(), color: look.motes, size: 0.22, transparent: true, opacity: 0.85, blending: AdditiveBlending, depthWrite: false, toneMapped: false }),
    );
    this.motes.frustumCulled = false;
    scene.add(this.motes);

    if (options.decor) scene.add(this.decor);
  }

  /** Przesuwa teren, niebo, światło i dekoracje za punktem (x, z). */
  follow(x: number, z: number): void {
    this.center.set(x, 0, z);
    this.ground.position.set(x, 0, z);
    this.sky.position.set(x, 0, z);
    // Mapa cienia przesuwa się skokami o cały piksel mapy (w układzie słońca): przy płynnym przesuwaniu
    // brzegi cieni na trawie przeskakiwały co klatkę i „mrówkowały” kropkami razem z bohaterem.
    const sc = this.sun.shadow.camera;
    const texel = (sc.right - sc.left) / this.sun.shadow.mapSize.x;
    const a = Math.round((x * SUN_RIGHT.x + z * SUN_RIGHT.z) / texel) * texel - (x * SUN_RIGHT.x + z * SUN_RIGHT.z);
    const b = Math.round((x * SUN_UP.x + z * SUN_UP.z) / texel) * texel - (x * SUN_UP.x + z * SUN_UP.z);
    // Przesunięcie po ziemi, które w układzie słońca daje dokładnie (a, b) — rozwiązanie układu 2×2.
    const det = SUN_RIGHT.x * SUN_UP.z - SUN_RIGHT.z * SUN_UP.x;
    const sx = x + (a * SUN_UP.z - b * SUN_RIGHT.z) / det;
    const sz = z + (b * SUN_RIGHT.x - a * SUN_UP.x) / det;
    this.sun.position.set(sx - 14, 26, sz + 10);
    this.sun.target.position.set(sx, 0, sz);
    this.back.position.set(x + 10, 12, z - 16);
    this.back.target.position.set(x, 0, z);
    this.updateChunks(x, z);
  }

  /** Rozmiar drobin klimatu zależy od rozdzielczości — scena przekazuje go przy zmianie okna. */
  setViewport(heightPx: number, fovDeg: number): void {
    this.ambience?.setViewport(heightPx, fovDeg);
  }

  private updateAmbience(dt: number): void {
    if (!this.ambience) return;
    const snow = this.ambienceKind === 'snow';
    // Stała liczba nowych drobin na sekundę, niezależnie od FPS.
    this.ambienceCarry += dt * (snow ? 40 : 14);
    const p = new Vector3();
    while (this.ambienceCarry >= 1) {
      this.ambienceCarry -= 1;
      const x = this.center.x + (Math.random() - 0.5) * 30;
      const z = this.center.z + (Math.random() - 0.5) * 26;
      if (snow) {
        p.set(x, 6 + Math.random() * 4, z);
        this.ambience.emit(p, { color: '#f4faff', count: 1, speed: 0.35, life: 6, up: -1.1, size: 0.09, drag: 0.4 });
      } else {
        p.set(x, 7 + Math.random() * 3, z);
        const leaf = ['#e8913a', '#d4622a', '#e8c04a', '#b8562a'][Math.floor(Math.random() * 4)]!;
        this.ambience.emit(p, { color: leaf, count: 1, speed: 0.6, life: 7, up: -1.3, size: 0.2, drag: 0.2 });
      }
    }
    this.ambience.update(dt);
  }

  update(dt: number): void {
    this.time += dt;
    this.updateAmbience(dt);
    this.wind.value = this.time;
    (this.sky.material as ShaderMaterial).uniforms.uTime!.value = this.time;
    const pos = this.motes.geometry.attributes.position as BufferAttribute;
    for (let i = 0; i < MOTES; i++) {
      const s = this.moteSeeds.subarray(i * 4, i * 4 + 4);
      const t = this.time * 0.3 + s[3]!;
      // Drobiny „zawijają się” wokół kamery, więc zawsze są w kadrze.
      const wrap = (v: number, c: number) => c + ((((v - c + 15) % 30) + 30) % 30) - 15;
      pos.setXYZ(
        i,
        wrap(s[0]! + Math.sin(t) * 1.5 + this.time * 0.2, this.center.x),
        s[1]! + Math.sin(t * 1.7) * 0.4,
        wrap(s[2]! + Math.cos(t * 0.8) * 1.5, this.center.z),
      );
    }
    pos.needsUpdate = true;
    (this.motes.material as PointsMaterial).opacity = 0.6 + 0.3 * Math.sin(this.time * 2.3);
  }

  private updateChunks(x: number, z: number): void {
    const cx = Math.floor(x / CHUNK);
    const cz = Math.floor(z / CHUNK);
    const wanted = new Set<string>();
    for (let i = -2; i <= 2; i++) {
      // Kamera patrzy pod kątem, więc dalej w głąb (−z) widać więcej — tam ładujemy dodatkowy rząd.
      for (let j = -3; j <= 2; j++) {
        const key = `${cx + i},${cz + j}`;
        wanted.add(key);
        if (!this.chunks.has(key)) {
          const chunk = this.buildChunk(cx + i, cz + j);
          this.chunks.set(key, chunk);
          this.decor.add(chunk);
        }
      }
    }
    for (const [key, chunk] of this.chunks) {
      if (wanted.has(key)) continue;
      chunk.removeFromParent();
      for (const c of chunk.children) if ((c as InstancedMesh).isInstancedMesh) (c as InstancedMesh).dispose();
      this.chunks.delete(key);
    }
  }

  private buildChunk(cx: number, cz: number): Group {
    const group = new Group();
    const m = new Matrix4();
    const q = new Quaternion();
    const s = new Vector3();
    const p = new Vector3();
    const up = new Vector3(0, 1, 0);

    const rocks = new InstancedMesh(this.rockGeo, this.rockMat, this.props.length ? 3 : 6);
    for (let i = 0; i < rocks.count; i++) {
      p.set((cx + hash(cx, cz, i)) * CHUNK, 0.05, (cz + hash(cz, cx, i + 50)) * CHUNK);
      q.setFromAxisAngle(up, hash(cx, cz, i + 100) * Math.PI * 2);
      const k = 0.5 + hash(cx, cz, i + 150) * 1.4;
      s.set(k, k * (0.5 + hash(cx, cz, i + 200) * 0.4), k);
      rocks.setMatrixAt(i, m.compose(p, q, s));
    }
    rocks.castShadow = rocks.receiveShadow = true;

    // Trawa w kępach, nie równomiernie — wygląda naturalniej.
    const grass = new InstancedMesh(this.grassGeo, this.grassMat, 180);
    grass.userData.noAo = true; // bez AO: kropki szumu na źdźbłach jechały z kamerą (stage.ts, SolidAoPass)
    for (let i = 0; i < grass.count; i++) {
      const clump = Math.floor(i / 9);
      const ox = (cx + hash(cx, cz, clump + 300)) * CHUNK;
      const oz = (cz + hash(cz, cx, clump + 400)) * CHUNK;
      p.set(ox + (hash(cx, cz, i + 2000) - 0.5) * 1.2, 0, oz + (hash(cz, cx, i + 2100) - 0.5) * 1.2);
      // Wokół środka (podest w obozie, start wyprawy) trawa nie rośnie — chowamy źdźbło pod ziemię.
      if (Math.hypot(p.x, p.z) < 2.4) p.y = -5;
      q.setFromAxisAngle(up, hash(cx, cz, i + 500) * Math.PI);
      const k = 0.5 + hash(cx, cz, i + 600) * 0.9;
      s.set(k, k * (0.8 + hash(cx, cz, i + 650) * 0.6), k);
      grass.setMatrixAt(i, m.compose(p, q, s));
    }
    grass.receiveShadow = true;
    group.add(rocks, grass);
    this.placeProps(group, cx, cz);
    return group;
  }

  /** Promień podstawy modelu w jednostkach sceny przy skali 1 (do dopasowania do kolizji). */
  private footprintOf(path: string, assets: Assets): number {
    let r = this.footprint.get(path);
    if (r === undefined) {
      const size = new Box3().setFromObject(assets.template(path).scene).getSize(new Vector3());
      r = Math.max(0.05, Math.max(size.x, size.z) / 2);
      this.footprint.set(path, r);
    }
    return r;
  }

  private addProp(group: Group, def: PropDef, assets: Assets, x: number, z: number, rot: number, scale: number): void {
    const obj = assets.template(def.path).scene.clone();
    obj.position.set(x, 0, z);
    obj.rotation.y = rot;
    obj.scale.setScalar(scale);
    obj.traverse((o) => {
      o.castShadow = !!def.shadow;
      o.receiveShadow = true;
    });
    group.add(obj);
    if (def.glow) {
      const spots = (group.userData.spots ??= []) as LightSpot[];
      spots.push({ pos: new Vector3(x, def.glow.height * scale, z), color: def.glow.color, intensity: def.glow.intensity });
    }
  }

  /** Świecące dekoracje w załadowanych kawałkach mapy (dla dynamicznych świateł). */
  lightSpots(): LightSpot[] {
    const out: LightSpot[] = [];
    for (const chunk of this.chunks.values()) {
      const spots = chunk.userData.spots as LightSpot[] | undefined;
      if (spots) out.push(...spots);
    }
    return out;
  }

  private placeProps(group: Group, cx: number, cz: number): void {
    if (!this.props.length) return;

    // 1. Przeszkody z silnika: w każdym miejscu model pasującej wielkości, przeskalowany do obrysu kolizji.
    if (this.obstacles) {
      for (const o of chunkObstacles(this.obstacles.seed, cx, cz, this.obstacles.count)) {
        const pool = this.props.filter((p) => p.def.block === o.size);
        if (!pool.length) continue;
        const total = pool.reduce((sum, p) => sum + p.def.weight, 0);
        let roll = o.variant * total;
        const pick = pool.find((p) => (roll -= p.def.weight) <= 0) ?? pool[0]!;
        const target = OBSTACLE_RADII[o.size] * WORLD_SCALE * 1.15;
        const natural = this.footprintOf(pick.def.path, pick.assets) * pick.def.scale;
        const scale = pick.def.scale * Math.min(1.5, Math.max(0.6, target / natural));
        this.addProp(group, pick.def, pick.assets, o.x * WORLD_SCALE, o.y * WORLD_SCALE, o.variant * Math.PI * 2 * 7, scale);
      }
    }

    // 2. Drobnica (bez kolizji). Bez przeszkód z silnika (obóz) — wszystkie dekoracje luzem.
    const decor = this.obstacles ? this.props.filter((p) => p.def.block === undefined) : this.props;
    if (!decor.length) return;
    const total = decor.reduce((sum, p) => sum + p.def.weight, 0);
    for (let i = 0; i < DECOR_PER_CHUNK; i++) {
      const x = (cx + hash(cx, cz, i + 700)) * CHUNK;
      const z = (cz + hash(cz, cx, i + 800)) * CHUNK;
      if (Math.hypot(x, z) < CLEAR_RADIUS) continue;
      let roll = hash(cx, cz, i + 900) * total;
      const pick = decor.find((p) => (roll -= p.def.weight) <= 0) ?? decor[0]!;
      this.addProp(group, pick.def, pick.assets, x, z, hash(cx, cz, i + 1000) * Math.PI * 2, pick.def.scale * (0.85 + hash(cx, cz, i + 1100) * 0.3));
    }
  }
}
